import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BidType, StatisticsResponse } from '../models/api-response.models';
import {
  IntegrationPartnerService,
  PartnerProfileResponse,
} from './integration-partner.service';
import { LocalStorageService } from './local-storage.service';
import {
  BidHistoryResponse,
  ChartPoint,
  DashboardResponse,
  SettlementResponse,
  UtilityService,
} from './utility.service';

export const INTEGRATION_PARTNER_ROLE = 'IntegrationPartner';

/**
 * Role-aware dashboard data source.
 *
 * Utility accounts keep the existing utility-wide endpoints; IntegrationPartner
 * operators get the partner-scoped endpoints, which resolve the partner from
 * the bearer token. Components stay identical for both roles.
 */
@Injectable({
  providedIn: 'root',
})
export class DashboardDataService {
  constructor(
    private utilityService: UtilityService,
    private partnerService: IntegrationPartnerService,
    private localStorageService: LocalStorageService
  ) { }

  isPartnerSession(): boolean {
    return this.localStorageService.getRole() === INTEGRATION_PARTNER_ROLE;
  }

  getPartnerProfile(): Observable<PartnerProfileResponse> {
    return this.partnerService.getProfile();
  }

  getDashboard(): Observable<DashboardResponse> {
    return this.isPartnerSession()
      ? this.partnerService.getDashboard()
      : this.utilityService.getUtilityDashboard();
  }

  getStatistics(
    start_date: string,
    end_date: string,
    bid_type: BidType = BidType.Preferential
  ): Observable<StatisticsResponse> {
    return this.isPartnerSession()
      ? this.partnerService.getStatistics(start_date, end_date, bid_type)
      : this.utilityService.getUtilityStatistics(start_date, end_date, bid_type);
  }

  getChart(
    start_date: string,
    end_date: string,
    bid_type: BidType = BidType.Preferential
  ): Observable<ChartPoint[]> {
    if (this.isPartnerSession()) {
      return this.partnerService.getChart(start_date, end_date, bid_type);
    }
    const utilityId = this.localStorageService.getUserId();
    return this.utilityService.getChart(utilityId, start_date, end_date, bid_type);
  }

  getBidHistory(
    start_date: string,
    end_date: string,
    limit: number,
    offset: number,
    bid_type?: string
  ): Observable<BidHistoryResponse> {
    if (this.isPartnerSession()) {
      return this.partnerService.getBidHistory(
        start_date, end_date, limit, offset, bid_type
      );
    }
    const utilityId = this.localStorageService.getUserId();
    return this.utilityService.getBidHistory(
      utilityId, start_date, end_date, limit, offset, bid_type
    );
  }

  getSettlementHistory(
    start_date: string,
    end_date: string
  ): Observable<SettlementResponse> {
    if (this.isPartnerSession()) {
      return this.partnerService.getSettlementHistory(start_date, end_date);
    }
    const utilityId = this.localStorageService.getUserId();
    return this.utilityService.getSettlementHistory(
      utilityId, start_date, end_date
    );
  }

  getImportExport(offset: number = 0): Observable<any> {
    return this.isPartnerSession()
      ? this.partnerService.getImportExport(offset)
      : this.utilityService.getImportExport(offset);
  }
}
